import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model.ts';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model.ts';
import { SpeciesV1 } from '@/model/Species.model.ts';
import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import { _notNil } from '@/littledash.ts';
import { errorToast, renderMetadataValue, successToast } from '@/helpers.tsx';
import { MetadataField } from '@/model/Metadata.model.ts';
import { modalAction } from '@/utils/modal';
import type { Study } from '@/model/Study.model';
import type { Dispatch } from 'redux';
import { ApiService } from '@/support/ApiService.ts';
import { Animal, AnimalApiId, AnimalSex, AnimalSexMap } from '@/model/Animal.model.ts';
import Link from '@/components/UI/Link';
import { web as webRoute } from '@/support/route.ts';
import { ID } from '@/model/Common.model.ts';
import { AnimalDefinitionApiId } from '@/model/PlannedAnimal.model.ts';
import { FilterTypes as filterType } from '@/constants/FilterTypes';

export interface PlannedAnimalDefinition {
  api_id: AnimalDefinitionApiId;
  animal_api_id: AnimalApiId | null;
  study_group_api_id: string;
  planned_id: string;
  sex: AnimalSex;
  species_api_id: string | null;
  strain_api_id: string | null;
  metadata: Array<{
    type: string;
    value: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface PlannedAnimalWithGroup extends PlannedAnimalDefinition {
  groupName?: string;
  species_name?: string;
  strain_name?: string;
}

export const plannedAnimalColumns = (
  groups: Array<TreatmentGroup>,
  species: Array<SpeciesV1>,
  metadataOptions: Array<MetadataField>,
  animals: Array<Animal>,
  studyId: ID
): Array<TanStackColumnDef<PlannedAnimalWithGroup>> => {
  const metadataColumns = getMetadataColumns(metadataOptions);
  return [
    {
      id: 'planned_id',
      header: 'Planned animal ID',
      accessorKey: 'planned_id',
      isVisible: true,
    },
    {
      id: 'group',
      header: 'Group',
      accessorKey: 'study_group_api_id',
      isVisible: true,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        const studyGroup = groups.find((group) => group.api_id === row.original.study_group_api_id);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return studyGroup ? <GroupLabel group={studyGroup} /> : null;
      },
    },
    {
      id: 'sex',
      header: 'Sex',
      accessorKey: 'sex',
      enableSorting: true,
      isVisible: true,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        return AnimalSexMap[row.original.sex] ?? row.original.sex.toUpperCase();
      },
    },
    {
      id: 'mapped_animal',
      header: 'Mapped animal',
      isVisible: true,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        const mappedAnimal = animals?.find((animal) => animal.api_id === row.original.animal_api_id);
        return (
          <div className="flex justify-between items-center w-100">
            {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
            {mappedAnimal ? (
              <Link
                to={webRoute('animals.show', {
                  id: studyId,
                  subject_id: mappedAnimal.api_id,
                })}
                className="link blue"
              >
                {mappedAnimal.name}
              </Link>
            ) : (
              <span className="text-muted">{'-'}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'species',
      header: 'Species',
      accessorKey: 'species_api_id',
      isVisible: true,
      enableSorting: false,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        const speciesItem = species.find((s) => s.api_id === row.original.species_api_id);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return speciesItem ? speciesItem.name : '-';
      },
    },
    {
      id: 'strain',
      header: 'Strain',
      accessorKey: 'strain_api_id',
      isVisible: true,
      enableSorting: false,
      cell: ({ row }: { row: { original: PlannedAnimalWithGroup } }) => {
        const speciesItem = species.find((s) => s.api_id === row.original.species_api_id);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!speciesItem || !row.original.strain_api_id) {
          return '-';
        }
        const strainItem = speciesItem?.strains?.find((s) => s.api_id === row.original.strain_api_id);
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        return strainItem ? strainItem.name : '-';
      },
    },
    ...metadataColumns,
  ];
};

const getMetadataColumns = (metadataOptions?: Array<MetadataField>) =>
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  metadataOptions?.map(({ id, title, glossary_api_id, field_type }) => ({
    id: String(id),
    header: title,
    isVisible: false,
    enableSorting: false,
    accessorFn: (row: PlannedAnimalWithGroup) => {
      const metadataItem = row.metadata.find((item) => item.type === glossary_api_id);
      return _notNil(metadataItem) ? renderMetadataValue({ value: metadataItem.value, field_type }) : '';
    },
  })) || [];

export const plannedAnimalBulkActions = (
  selectedFlatRows: Array<{ original: PlannedAnimalWithGroup }>,
  resetTableState: () => void,
  study: Study,
  dispatch: Dispatch,
  fetchPlannedAnimals: () => Promise<void>
) => {
  const { openModal, closeModal } = modalAction(dispatch);

  const handleCallback = async (text: string) => {
    await fetchPlannedAnimals();
    closeModal();
    resetTableState();
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (text) {
      successToast(text);
    }
  };

  const plannedAnimals = selectedFlatRows.map((row) => row.original);
  const handleDelete = async () => {
    try {
      const deletePromises = plannedAnimals.map((animal) =>
        ApiService.call({
          endpoint: 'DELETE /api/v1/studies/{studyApiId}/planned/animals/{animalDefinitionApiId}/remove',
          path: { studyApiId: study.api_id, animalDefinitionApiId: animal.api_id },
        })
      );
      await Promise.all(deletePromises);
      return handleCallback('Successfully deleted planned animals');
    } catch (error) {
      errorToast('There was a problem deleting planned animals, please try again later.');
    }
  };

  return [
    {
      name: 'Delete',
      action: () =>
        openModal('CONFIRM_DELETE_PLANNED_ANIMALS', {
          subject: plannedAnimals,
          closeModal,
          onClick: handleDelete,
        }),
      className: 'red',
    },
  ];
};

export const plannedAnimalFilterOptions = (
  groups: Array<TreatmentGroup>,
  species: Array<SpeciesV1>
): Array<FilterOption> => {
  const groupOptions = groups.map((group) => ({
    name: group.name,
    value: group.api_id,
  }));
  const groupFilters: FilterOption = {
    value: 'study_group',
    name: 'Group',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: groupOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: groupOptions,
      },
    ],
  };

  const sexOptions = Object.entries(AnimalSexMap).map(([value, name]) => ({
    name,
    value,
  }));
  const sexFilter: FilterOption = {
    value: 'sex',
    name: 'Sex',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: sexOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: sexOptions,
      },
    ],
  };

  const speciesOptions = species.map((s) => ({
    name: s.name,
    value: s.api_id,
  }));
  const speciesFilter: FilterOption = {
    value: 'species',
    name: 'Species',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: speciesOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: speciesOptions,
      },
    ],
  };

  const strainOptions = species.reduce<Array<{ name: string; value: string }>>((acc, s) => {
    if (_notNil(s.strains)) {
      s.strains.forEach((strain) => {
        acc.push({
          name: strain.name,
          value: strain.api_id,
        });
      });
    }
    return acc;
  }, []);
  const strainFilter: FilterOption = {
    value: 'strain',
    name: 'Strain',
    operations: [
      {
        name: 'is equal to',
        value: filterType.eq,
        options: strainOptions,
      },
      {
        name: 'is not equal to',
        value: filterType.ne,
        options: strainOptions,
      },
    ],
  };

  return [groupFilters, sexFilter, speciesFilter, strainFilter];
};
